import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Search } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Avatar from '../../components/ui/Avatar';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import Input, { Select } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { employees, stores } from '../../data/mockData';

const statusLabel = { online: 'Онлайн', offline: 'Офлайн', vacation: 'В отпуске' };

export default function EmployeeList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [filterStore, setFilterStore] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'Продавец', storeId: 's1', password: '' });

  const filtered = employees.filter(e => {
    if (search && !e.name.toLowerCase().includes(search.toLowerCase()) && !e.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStore && !e.storeIds.includes(filterStore)) return false;
    if (filterRole && e.role !== filterRole) return false;
    if (filterStatus && e.status !== filterStatus) return false;
    return true;
  });

  const columns = [
    {
      key: 'name', label: 'Сотрудник',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <Avatar name={row.name} size={34} />
            <span style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff',
              background: row.status === 'online' ? '#34C759' : row.status === 'vacation' ? '#FF9500' : '#D1D5DB',
            }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{row.name}</div>
            <div style={{ fontSize: 12, color: '#8E8E93' }}>{row.email}</div>
          </div>
        </div>
      )
    },
    { key: 'code', label: 'ID', render: v => <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#6B7280' }}>{v}</span> },
    { key: 'role', label: 'Роль', render: v => <Badge variant={v === 'Старший продавец' ? 'primary' : v === 'Стажёр' ? 'warning' : 'gray'}>{v}</Badge> },
    {
      key: 'storeIds', label: 'Магазины', sortable: false,
      render: (v) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {v.map(sid => {
            const s = stores.find(x => x.id === sid);
            return <Badge key={sid} variant="primary" style={{ fontSize: 10 }}>{s?.name || sid}</Badge>;
          })}
        </div>
      )
    },
    { key: 'status', label: 'Статус', render: v => <Badge variant={v}>{statusLabel[v]}</Badge> },
    {
      key: 'lastActivity', label: 'Последняя активность',
      render: v => <span style={{ color: '#8E8E93', fontSize: 12 }}>{new Date(v).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
    },
    { key: 'tasksCompleted', label: 'Задач (мес)', render: v => <span style={{ fontWeight: 700, color: '#4A6CF7' }}>{v}</span> },
    {
      key: 'actions', label: '', sortable: false,
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => { toast('Пароль сброшен', 'success'); }} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', color: '#6B7280' }}>Сбросить пароль</button>
          <button onClick={() => { toast(`Сотрудник ${row.status === 'online' ? 'деактивирован' : 'активирован'}`, 'success'); }} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', color: row.status === 'online' ? '#FF3B30' : '#34C759' }}>
            {row.status === 'online' ? 'Деактив.' : 'Активировать'}
          </button>
        </div>
      )
    }
  ];

  const handleCreate = () => {
    if (!form.name || !form.email) { toast('Заполните обязательные поля', 'error'); return; }
    toast(`Сотрудник ${form.name} создан`, 'success');
    setShowModal(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>Сотрудники</h1>
          <p style={{ color: '#8E8E93', fontSize: 13, marginTop: 2 }}>Всего: {employees.length} • Онлайн: {employees.filter(e => e.status === 'online').length}</p>
        </div>
        <Button icon={<UserPlus size={15} />} onClick={() => setShowModal(true)}>Добавить сотрудника</Button>
      </div>

      <Card padding="0">
        {/* Filters */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F5F6FA', borderRadius: 8, padding: '7px 12px', flex: 1, minWidth: 200 }}>
            <Search size={14} color="#9CA3AF" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск по имени / email..." style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', fontFamily: 'inherit' }} />
          </div>
          <select value={filterStore} onChange={e => setFilterStore(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Все магазины</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Все роли</option>
            {['Старший продавец', 'Продавец', 'Стажёр'].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Все статусы</option>
            <option value="online">Онлайн</option>
            <option value="offline">Офлайн</option>
            <option value="vacation">В отпуске</option>
          </select>
        </div>
        <Table columns={columns} data={filtered} onRowClick={row => navigate(`/employees/${row.id}`)} emptyText="Сотрудники не найдены" />
      </Card>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Добавить сотрудника">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="ФИО" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Иванов Иван Иванович" required />
          <Input label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@osg.uz" required />
          <Input label="Телефон" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+998 90 000 00 00" />
          <Select label="Роль" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} required options={[
            { value: 'Старший продавец', label: 'Старший продавец' },
            { value: 'Продавец', label: 'Продавец' },
            { value: 'Стажёр', label: 'Стажёр' },
          ]} />
          <Select label="Магазин" value={form.storeId} onChange={e => setForm(f => ({ ...f, storeId: e.target.value }))} required options={stores.map(s => ({ value: s.id, label: s.name }))} />
          <Input label="Пароль" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Минимум 8 символов" required />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Отмена</Button>
            <Button onClick={handleCreate}>Создать</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
